import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import * as repo from '@/db/repositories'
import { parseEpub, InvalidEpubError } from '@/epub/parser'
import { createId } from '@/utils/id'
import { sha256Hex } from '@/utils/hash'
import { alphaBucket } from '@/utils/alpha-bucket'
import { authorLastName } from '@/utils/author-sort-key'
import { invalidateCoverCache } from '@/components/library/cover-cache'
import { useDownloadEpub } from '@/composables/useDownloadEpub'
import { downloadSharedEpub, uploadEpub } from '@/sync/storage'
import { listCloudBooks } from '@/sync/cloud-index'
import { cloudBookToBook } from '@/sync/cloud-book-mapper'
import { useAuthStore } from '@/sync/auth'
import { track } from '@/composables/useEventTracker'
import { useSearchStore } from '@/stores/search'
import { useSettingsStore } from '@/stores/settings'
import { useShelvesStore } from '@/stores/shelves'
import { useFavoritesStore } from '@/stores/favorites'
import type { Book, ReadingStatus } from '@/types/domain'
import type { LibraryDensity } from '@/types/settings'

/** Criterios de ordenación disponibles en la biblioteca. */
export type SortKey =
  | 'recent' // más recientemente añadidos primero (dateAdded)
  | 'title' // alfabético por título
  | 'author' // alfabético por autor
  | 'progress' // más avanzados primero
  | 'year' // año de publicación (metadata.publishDate), más reciente primero
  | 'rating' // mejor valorados primero
  | 'lastRead' // última lectura (progress.lastReadAt), más reciente primero

/** Filtro por estado de lectura. `all` muestra todo. */
export type StatusFilter = ReadingStatus | 'all'

/** Resultado de importar un lote de archivos. */
export interface ImportReport {
  imported: number
  /** Archivos saltados porque ya existen (mismo contentHash). */
  skipped: { filename: string; reason: string; existingTitle: string }[]
  failed: { filename: string; reason: string }[]
}

/**
 * Store de la biblioteca.
 *
 * Responsable de cargar libros, importar EPUBs (parseo + persistencia),
 * y exponer listas derivadas con búsqueda, filtro por estado y orden.
 * El filtrado se hace en memoria con `computed` para mantener la UI
 * reactiva sin recargas.
 */
export const useLibraryStore = defineStore('library', () => {
  const books = ref<Book[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const importing = ref(false)

  /**
   * Libros que están en la nube (Supabase `cloud_books`) pero que aún **no**
   * se han descargado a este dispositivo. Se representan como `Book` "sin
   * blob" (sin `fileBlob`/`coverBlob`) para poder fusionarlos con los
   * locales y aplicar filtros/orden uniformes.
   *
   * Se cargan en `load()` cuando el sync está activo y hay sesión; si no,
   * queda vacío y la Biblioteca opera 100% local como antes.
   */
  const cloudBooks = ref<Book[]>([])
  const cloudLoading = ref(false)

  /**
   * IDs de los libros locales (materializados en IndexedDB). Sirve para:
   *  - saber qué `CloudBook` ya no hace falta mostrar como "solo nube",
   *  - indicar a `BookCard` si un libro está en el dispositivo.
   */
  const localIds = computed(() => new Set(books.value.map((b) => b.id)))
  /** IDs de los libros cloud (los de `cloudBooks`, no descargados aquí). */
  const cloudBookIds = computed(() => new Set(cloudBooks.value.map((b) => b.id)))

  /**
   * Lista unificada: locales primero y, a continuación, los cloud que no
   * estén ya presentes localmente (sin duplicar). Es la fuente que consumen
   * todos los `computed` derivados (filtros, orden, secciones curadas),
   * para que la Biblioteca muestre lo mismo en cualquier dispositivo.
   *
   * Cuando hay colisión de id, gana la versión local (tiene los datos
   * completos y los blobs materializados).
   */
  const unifiedBooks = computed<Book[]>(() => {
    const seen = new Set<string>()
    const seenHashes = new Set<string>()
    const out: Book[] = []
    for (const b of books.value) {
      if (seen.has(b.id)) continue
      seen.add(b.id)
      if (b.contentHash) seenHashes.add(b.contentHash)
      out.push(b)
    }
    for (const b of cloudBooks.value) {
      if (seen.has(b.id)) continue
      // Dedup por contentHash: el mismo EPUB importado en dos dispositivos
      // tiene ids distintos, pero el hash del archivo es estable. Así la
      // versión local gana también en ese caso (no aparece duplicada).
      if (b.contentHash && seenHashes.has(b.contentHash)) continue
      seen.add(b.id)
      if (b.contentHash) seenHashes.add(b.contentHash)
      out.push(b)
    }
    return out
  })

  // Filtros / orden reactivos, manipulados por la UI.
  const search = ref('')
  const statusFilter = ref<StatusFilter>('all')
  const sortKey = ref<SortKey>('recent')
  /** Etiqueta activa para filtrar (null = todas). */
  const tagFilter = ref<string | null>(null)
  /** Colección activa para filtrar (null = todas). */
  const collectionFilter = ref<string | null>(null)
  /** Estantería activa para filtrar (null = todas). */
  const shelfFilter = ref<string | null>(null)
  /** Toggle del chip "Compartidos conmigo": cuando es true, sólo libros
   * llegados vía invitación (`shareId !== null`). */
  const sharedFilter = ref(false)
  /** Toggle del botón "Favoritos" en el hero: cuando es true, sólo libros
   * marcados como favoritos. Mutuamente excluyente con sharedFilter. */
  const favoritesFilter = ref(false)
  /**
   * Letra activa del índice alfabético (A-Z) o `'#'` para "otros". Null =
   * sin filtro de letra (todas). No persiste entre sesiones (como
   * sharedFilter/favoritesFilter).
   */
  const alphaFilter = ref<string | null>(null)
  /**
   * Modo del índice alfabético: `'title'` agrupa por primera letra del
   * título, `'author'` por la del apellido del autor (ver
   * `authorLastName`). No persiste entre sesiones.
   */
  const alphaMode = ref<'title' | 'author'>('title')

  /**
   * Densidad de la rejilla. Derivado de settings (persistido). El setter
   * actualiza settings, que a su vez persiste.
   */
  const density = computed<LibraryDensity>({
    get: () => useSettingsStore().settings.libraryFilters.density,
    set: (v) => {
      void useSettingsStore().update({ libraryFilters: { density: v } })
    },
  })

  /** Todas las etiquetas usadas en la biblioteca, con conteo. */
  const allTags = computed<{ tag: string; count: number }[]>(() => {
    const map = new Map<string, number>()
    for (const b of unifiedBooks.value) {
      for (const t of b.metadata.tags ?? []) {
        map.set(t, (map.get(t) ?? 0) + 1)
      }
    }
    return [...map.entries()]
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count)
  })

  /** Todas las colecciones de la biblioteca, con conteo. */
  const allCollections = computed<{ name: string; count: number }[]>(() => {
    const map = new Map<string, number>()
    for (const b of unifiedBooks.value) {
      if (b.metadata.collection) {
        map.set(b.metadata.collection, (map.get(b.metadata.collection) ?? 0) + 1)
      }
    }
    return [...map.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  })

  /**
   * Último libro con progreso de lectura iniciado (no terminado), para la
   * tarjeta "Continúa leyendo" del Home. Orden O(n) en memoria por
   * `progress.lastReadAt` (no hay índice Dexie para ese campo).
   */
  const continueReading = computed<Book | null>(() => {
    const candidates = unifiedBooks.value
      .filter((b) => b.progress.percent > 0 && b.status !== 'finished' && b.progress.lastReadAt)
      .sort((a, b) => (b.progress.lastReadAt ?? '').localeCompare(a.progress.lastReadAt ?? ''))
    return candidates[0] ?? null
  })

  /** Libros sin empezar (status 'unread'), para la sección "Sin empezar". */
  const notStarted = computed<Book[]>(() => unifiedBooks.value.filter((b) => b.status === 'unread'))

  /**
   * Libros en curso (status 'reading'), ordenados por última lectura desc,
   * para la sección "Leyendo" de la vista curada.
   *
   * Incluye el libro del héroe "Continúa leyendo": aunque ya tiene su tarjeta
   * destacada arriba, debe aparecer también en la rejilla para que el usuario
   * vea TODOS sus libros en cada sección (antes se excluía para "no duplicar",
   * pero eso hacía que el libro en curso pareciera desaparecido de la biblioteca).
   */
  const inProgress = computed<Book[]>(() =>
    unifiedBooks.value
      .filter((b) => b.status === 'reading')
      .sort((a, b) => (b.progress.lastReadAt ?? '').localeCompare(a.progress.lastReadAt ?? '')),
  )

  /**
   * Libros terminados recientemente, ordenados por fecha de finalización
   * desc, para la sección "Terminados recientemente".
   */
  const recentlyFinished = computed<Book[]>(() =>
    unifiedBooks.value
      .filter((b) => b.status === 'finished' && b.dateFinished)
      .sort((a, b) => (b.dateFinished ?? '').localeCompare(a.dateFinished ?? '')),
  )

  /** Libros marcados como favoritos por el usuario. */
  const favorites = computed<Book[]>(() => unifiedBooks.value.filter((b) => b.favorite))

  /** Libros llegados vía invitación (lectura compartida, F1.1). */
  const sharedWithMe = computed<Book[]>(() => unifiedBooks.value.filter((b) => b.shareId !== null))

  /**
   * Haystack de búsqueda por bookId (title + author + tags, lowercased).
   * Memoizado: solo se recalcula cuando cambia `unifiedBooks.value`, no en
   * cada recompute de `visibleBooks` (que ocurre al mover cualquier
   * filtro/orden). Antes esto se rebuildaba por libro dentro del computed
   * en cada cambio.
   */
  const searchHaystacks = computed<Map<string, string>>(() => {
    const map = new Map<string, string>()
    for (const b of unifiedBooks.value) {
      map.set(
        b.id,
        [b.title, b.author, ...(b.metadata.tags ?? [])].join(' ').toLowerCase(),
      )
    }
    return map
  })

  /**
   * Letras (buckets A-Z / '#') que tienen al menos un libro en la
   * biblioteca, según el modo activo (`alphaMode`). Lo usa `AlphabetBar`
   * para desactivar los botones sin libros (no se puede pulsar "Q" si
   * ningún título/autor va a Q).
   */
  const availableLetters = computed<Set<string>>(() => {
    const byAuthor = alphaMode.value === 'author'
    const s = new Set<string>()
    for (const b of unifiedBooks.value) {
      const key = byAuthor ? authorLastName(b.author) : b.title
      s.add(alphaBucket(key))
    }
    return s
  })

  /**
   * Al cambiar de modo (títulos↔autores), si la letra activa ya no existe
   * en el nuevo conjunto disponible, se resetea a null. Evita dejar un
   * filtro colgando que mostraría cero resultados.
   */
  watch([alphaMode], () => {
    if (alphaFilter.value && !availableLetters.value.has(alphaFilter.value)) {
      alphaFilter.value = null
    }
  })

  /** Lista filtrada y ordenada que consume la vista. */
  const visibleBooks = computed<Book[]>(() => {
    let list = unifiedBooks.value

    if (statusFilter.value !== 'all') {
      list = list.filter((b) => b.status === statusFilter.value)
    }

    if (tagFilter.value) {
      list = list.filter((b) => (b.metadata.tags ?? []).includes(tagFilter.value!))
    }

    if (collectionFilter.value) {
      list = list.filter((b) => b.metadata.collection === collectionFilter.value)
    }

    if (shelfFilter.value) {
      // Filtrado por estantería: un libro pasa si pertenece a la estantería
      // activa. Se consulta al store de shelves (lazy para evitar ciclo).
      const ids = useShelvesStore().bookIdSet(shelfFilter.value)
      list = list.filter((b) => ids.has(b.id))
    }

    if (sharedFilter.value) {
      list = list.filter((b) => b.shareId !== null)
    }

    if (favoritesFilter.value) {
      list = list.filter((b) => b.favorite)
    }

    // Filtro por letra del índice alfabético (A-Z / '#'), según el modo
    // activo (título o apellido del autor). Se aplica tras los filtros de
    // categoría y antes de la búsqueda libre, para que ambos se combinen
    // correctamente (letra AND búsqueda).
    if (alphaFilter.value) {
      const byAuthor = alphaMode.value === 'author'
      list = list.filter((b) => {
        const key = byAuthor ? authorLastName(b.author) : b.title
        return alphaBucket(key) === alphaFilter.value
      })
    }

    const q = search.value.trim().toLowerCase()
    if (q) {
      // Haystacks memoizados en `searchHaystacks` (no se rebuildan por libro
      // en cada recompute de filtros).
      const hay = searchHaystacks.value
      list = list.filter((b) => (hay.get(b.id) ?? '').includes(q))
    }

    const sorted = [...list]
    switch (sortKey.value) {
      case 'title':
        sorted.sort((a, b) => a.title.localeCompare(b.title, 'es', { sensitivity: 'base' }))
        break
      case 'author':
        sorted.sort((a, b) => a.author.localeCompare(b.author, 'es', { sensitivity: 'base' }))
        break
      case 'progress':
        // Más avanzados primero.
        sorted.sort((a, b) => b.progress.percent - a.progress.percent)
        break
      case 'year': {
        // Año de publicación (metadata.publishDate), más reciente primero.
        // Los libros sin fecha quedan al final.
        const yearOf = (b: Book): number => {
          const d = b.metadata.publishDate
          if (!d) return Number.NEGATIVE_INFINITY
          const y = parseInt(d.slice(0, 4), 10)
          return Number.isNaN(y) ? Number.NEGATIVE_INFINITY : y
        }
        sorted.sort((a, b) => yearOf(b) - yearOf(a))
        break
      }
      case 'rating':
        // Mejor valorados primero.
        sorted.sort((a, b) => b.rating - a.rating)
        break
      case 'lastRead':
        // Última lectura más reciente primero. Los no leídos al final.
        sorted.sort((a, b) =>
          (b.progress.lastReadAt ?? '').localeCompare(a.progress.lastReadAt ?? ''),
        )
        break
      case 'recent':
      default:
        sorted.sort((a, b) => b.dateAdded.localeCompare(a.dateAdded))
        break
    }
    return sorted
  })

  /**
   * Paginación "Cargar más" para la sección "Toda la biblioteca".
   * `visibleBooksPaged` es el slice que la UI renderiza; `loadMore()`
   * incrementa el límite. El límite se resetea a `PAGE_SIZE` cuando cambian
   * los filtros/orden, para que un cambio de criteria no deje al usuario
   * viendo un slice enorme de la lista anterior.
   */
  const PAGE_SIZE = 50
  const visibleLimit = ref(PAGE_SIZE)
  /** Slice actual de `visibleBooks` que la UI debe renderizar. */
  const visibleBooksPaged = computed<Book[]>(() => visibleBooks.value.slice(0, visibleLimit.value))
  /** ¿Quedan más libros por cargar? */
  const hasMoreToLoad = computed(() => visibleLimit.value < visibleBooks.value.length)
  /** Incrementa el límite en `PAGE_SIZE`. */
  function loadMore() {
    visibleLimit.value += PAGE_SIZE
  }
  // Reset al cambiar cualquier criterio que afecte a visibleBooks.
  watch([visibleBooks], () => {
    visibleLimit.value = PAGE_SIZE
  })

  /**
   * Carga el índice de libros cloud (`cloud_books`) y los fusiona como
   * `Book` "sin blob" en `cloudBooks`, excluyendo los que ya están
   * descargados localmente (para no duplicarlos en `unifiedBooks`).
   *
   * No se ejecuta si el sync está apagado o no hay sesión: en ese caso la
   * Biblioteca opera 100% local como antes. Los errores no bloquean la
   * carga local: se deja `cloudBooks` vacío y se loguea.
   */
  async function loadCloudBooks(): Promise<void> {
    const settings = useSettingsStore()
    const auth = useAuthStore()
    if (!settings.settings.sync.enabled || !auth.isAuthenticated) {
      cloudBooks.value = []
      return
    }
    cloudLoading.value = true
    try {
      const rows = await listCloudBooks()
      // El set CRDT de favoritos es la fuente cross-device del flag
      // `favorite` (cloud_books no lo guarda). Lazy lookup para evitar
      // dependencia circular de stores.
      const favorites = useFavoritesStore()
      // Leemos `books.value` (no la computed `localIds`) DESPUÉS del await:
      // en `load()` se llama en paralelo con `repo.getAllBooks()`, así que
      // al llegar aquí los locales ya están cargados y podemos filtrar los
      // cloud que ya están en el dispositivo sin duplicarlos.
      //
      // Se deduplica por `id` Y por `contentHash`: cuando el mismo EPUB se
      // importa en dos dispositivos cada uno genera un id distinto, pero el
      // hash del archivo es el mismo y permite detectar el duplicado. Gana
      // la versión local.
      const local = new Set(books.value.map((b) => b.id))
      const localHashes = new Set<string>()
      for (const b of books.value) {
        if (b.contentHash) localHashes.add(b.contentHash)
      }
      cloudBooks.value = rows
        .filter((r) => !local.has(r.id) && !(r.content_hash && localHashes.has(r.content_hash)))
        .map((r) => cloudBookToBook(r, favorites.isFavorite(r.id)))
    } catch (e) {
      // No rompemos la Biblioteca local por un fallo de nube.
      cloudBooks.value = []
      console.error('[library] carga de la nube falló:', e)
    } finally {
      cloudLoading.value = false
    }
  }

  /**
   * Race de arranque: `main.ts` monta la app SIN esperar a `auth.init()`
   * (optimización de LCP), así que `load()` —y su `loadCloudBooks()`— puede
   * correr mientras la sesión aún no se ha restaurado. En ese caso el guard
   * de arriba abandona con `cloudBooks = []`, y sin este watcher la
   * Biblioteca unificada se quedaría para siempre solo con los libros
   * descargados (filtros y orden operando sobre una lista parcial).
   *
   * Cuando la sesión aparece (o desaparece), recargamos (o vaciamos) el
   * índice cloud por sí mismos; las vistas no necesitan reaccionar.
   */
  watch(
    () => {
      const settings = useSettingsStore()
      const auth = useAuthStore()
      return settings.settings.sync.enabled && auth.isAuthenticated
    },
    (ready) => {
      if (ready) void loadCloudBooks()
      else cloudBooks.value = []
    },
  )

  async function load() {
    loading.value = true
    error.value = null
    // Restaurar filtros persistidos antes de cargar, para que la primera
    // renderización ya los aplique (sin parpadeo de "todos").
    const settings = useSettingsStore()
    const f = settings.settings.libraryFilters
    search.value = f.search
    statusFilter.value = f.statusFilter
    sortKey.value = f.sortKey
    tagFilter.value = f.tagFilter
    collectionFilter.value = f.collectionFilter
    shelfFilter.value = f.shelfFilter
    try {
      // Cargar estanterías y libros locales en paralelo (v18), y los cloud
      // DESPUÉS: `loadCloudBooks` filtra los cloud que ya están en el
      // dispositivo usando `books.value`, así que necesita que los locales
      // se hayan cargado primero para no duplicarlos en `unifiedBooks`.
      await Promise.all([
        useShelvesStore().loadAll(),
        repo.getAllBooks().then((b) => (books.value = b)),
      ])
      // No esperamos al cloud con `await` para que la biblioteca local se
      // pinte cuanto antes: la carga cloud es best-effort y actualiza
      // `cloudBooks` (reactivo) cuando termine.
      void loadCloudBooks()
    } catch (e) {
      error.value = messageOf(e)
    } finally {
      loading.value = false
    }
  }

  /**
   * Importa uno o varios archivos EPUB. Devuelve un informe con
   * éxitos, duplicados saltados y fallos por archivo.
   *
   * La detección de duplicados usa el hash SHA-256 del archivo: si un
   * libro existente tiene el mismo `contentHash`, se salta (no se añade
   * dos veces) y se informa al usuario.
   */
  async function importFiles(files: File[]): Promise<ImportReport> {
    importing.value = true
    const report: ImportReport = { imported: 0, skipped: [], failed: [] }
    try {
      // Índice de hashes existentes para detectar duplicados rápido.
      // Map: contentHash → { id, title }.
      const existingByHash = new Map<string, { id: string; title: string }>()
      for (const b of books.value) {
        if (b.contentHash) existingByHash.set(b.contentHash, { id: b.id, title: b.title })
      }

      // Detectar libros huérfanos (sin fileBlob en bookFiles) para poder
      // restaurarlos al reimportar. Caso típico: libros pre-migración v18
      // cuyo blob se perdió por el bug del upgrade (corregido en v19).
      const orphanIds = new Set<string>()
      for (const [, info] of existingByHash) {
        const file = await repo.getBookFile(info.id)
        if (!file) orphanIds.add(info.id)
      }

      for (const file of files) {
        try {
          const hash = await sha256Hex(file)
          const existing = existingByHash.get(hash)
          if (existing !== undefined) {
            // Si el libro existe pero es huérfano (sin archivo), restaurar
            // el blob en vez de saltarlo. Conserva metadatos/progreso del
            // libro original.
            if (orphanIds.has(existing.id)) {
              await repo.saveBookFile(existing.id, file)
              orphanIds.delete(existing.id)
              report.imported++
              continue
            }
            report.skipped.push({
              filename: file.name,
              reason: 'duplicate',
              existingTitle: existing.title,
            })
            continue
          }
          const book = await importOne(file, hash)
          await persistBookWithBlobs(book)
          maybeAutoUpload(book)
          books.value = [stripBlobs(book), ...books.value.filter((b) => b.id !== book.id)]
          existingByHash.set(hash, { id: book.id, title: book.title })
          // Indexar texto para la búsqueda global (fire-and-forget).
          void useSearchStore().indexBook(book, file)
          // Métrica global (panel admin): libro importado. Sin contenido sensible.
          void track('book_imported', { bookId: book.id, language: book.metadata.language })
          report.imported++
        } catch (e) {
          report.failed.push({ filename: file.name, reason: messageOf(e) })
        }
      }
    } finally {
      importing.value = false
    }
    return report
  }

  /**
   * Importa un único EPUB desde una URL remota (p. ej. un feed OPDS).
   *
   * Descarga el binario (con auto-detección proxy↔directo vía
   * `useDownloadEpub`), lo deduplica por `contentHash` igual que
   * `importFiles`, lo parsea y persiste. Devuelve un `ImportReport` con un
   * único resultado entre `imported`/`skipped`/`failed`.
   */
  async function importFromUrl(
    url: string,
    filename: string,
  ): Promise<ImportReport> {
    const report: ImportReport = { imported: 0, skipped: [], failed: [] }
    importing.value = true
    try {
      const { downloadEpub } = useDownloadEpub()
      const blob = await downloadEpub(url)
      const hash = await sha256Hex(blob)

      const existingTitle = books.value.find((b) => b.contentHash === hash)?.title
      if (existingTitle !== undefined) {
        report.skipped.push({ filename, reason: 'duplicate', existingTitle })
        return report
      }

      const book = await importOne(blob, hash)
      await persistBookWithBlobs(book)
      maybeAutoUpload(book)
      books.value = [stripBlobs(book), ...books.value.filter((b) => b.id !== book.id)]
      // Indexar texto para la búsqueda global (fire-and-forget).
      void useSearchStore().indexBook(book, blob)
      // Métrica global (panel admin): libro importado. Sin contenido sensible.
      void track('book_imported', { bookId: book.id, language: book.metadata.language })
      report.imported++
    } catch (e) {
      report.failed.push({ filename, reason: messageOf(e) })
    } finally {
      importing.value = false
    }
    return report
  }

  /**
   * Añade a la biblioteca local un libro compartido recibido vía invitación
   * (F1.1). Descarga el EPUB desde el storage compartido
   * (`shared/<shareId>/<ownerBookId>.epub`), lo parsea y lo persiste con
   * `shareId` poblado para que el filtro "Compartidos conmigo" lo muestre y
   * `ReaderView` sepa a qué room CRDT conectarse.
   *
   * Idempotente: si ya existe un libro con este `shareId`, no redescarga.
   *
   * @returns el `bookId` (id local nuevo) del libro compartido.
   */
  async function addSharedBook(shareId: string, ownerBookId: string, _title: string): Promise<string> {
    // Idempotencia: si ya tenemos el libro compartido, no redescargar.
    const existing = books.value.find((b) => b.shareId === shareId)
    if (existing) return existing.id

    const blob = await downloadSharedEpub(shareId, ownerBookId)
    const hash = await sha256Hex(blob)
    const book = await importOne(blob, hash)
    // Marcar como compartido y usar el título que viene del share (el del
    // owner), que es más fiable que el del parseo si el EPUB tuviera
    // metadatos pobres. Conservamos el resto del parseo (portada, etc.).
    book.shareId = shareId

    await persistBookWithBlobs(book)
    maybeAutoUpload(book)
    books.value = [stripBlobs(book), ...books.value.filter((b) => b.id !== book.id)]
    // Indexar texto para la búsqueda global (fire-and-forget).
    void useSearchStore().indexBook(book, blob)
    // Métrica global (panel admin). Sin contenido sensible.
    void track('book_imported', { bookId: book.id, language: book.metadata.language, shared: true })
    return book.id
  }

  /**
   * Persiste un libro ya construido que llega con sus blobs inline
   * (`fileBlob` / `coverBlob`), p. ej. una descarga desde la nube.
   *
   * Aplica el mismo patrón que la importación: guarda los blobs en sus
   * tablas propias (`bookFiles` / `bookCovers`) y la metadata sin blobs
   * inline en `books`, luego actualiza el estado en memoria (también sin
   * blobs). Así el libro sobrevive a recargas: el reader y `BookCover`
   * recuperan el archivo y la portada bajo demanda desde IndexedDB.
   *
   * Si ya existe un libro con el mismo id, se reemplaza (upsert).
   */
  async function addDownloadedBook(book: Book): Promise<string> {
    await persistBookWithBlobs(book)
    books.value = [stripBlobs(book), ...books.value.filter((b) => b.id !== book.id)]
    // Si el libro venía de la nube (estaba en `cloudBooks` como "sin blob"),
    // lo sacamos de ahí: la versión local (con blob) ya está en `books` y
    // `unifiedBooks` la prefiere, pero mantenerlo en ambos lados es redundante.
    if (cloudBooks.value.some((b) => b.id === book.id)) {
      cloudBooks.value = cloudBooks.value.filter((b) => b.id !== book.id)
    }
    // Indexar texto para la búsqueda global (fire-and-forget), si hay archivo.
    if (book.fileBlob) {
      void useSearchStore().indexBook(book, book.fileBlob)
    }
    return book.id
  }

  async function remove(id: string) {
    await repo.deleteBook(id)
    books.value = books.value.filter((b) => b.id !== id)
    // Limpiar el índice de búsqueda y el caché de portada del libro borrado.
    void useSearchStore().removeBookFromIndex(id)
    invalidateCoverCache(id)
    // Nota: si el libro sigue en la nube, no vuelve a aparecer como
    // cloud-only hasta la próxima recarga (`loadCloudBooks`). El borrado
    // local intencional no debería "resucitarlo" de inmediato.
  }

  /**
   * Borra varios libros a la vez. Usa una sola transacción Dexie (mucho
   * más eficiente que N `remove` individuales) y limpia el índice de
   * búsqueda de todos los libros borrados.
   *
   * Si `options.deleteCloud` es true, también borra las copias en la nube
   * (Storage + cloud_books) de los libros. Fire-and-forget: el borrado
   * local no se bloquea si falla el de nube.
   */
  async function removeMany(ids: string[], options: { deleteCloud?: boolean } = {}) {
    if (ids.length === 0) return
    const idSet = new Set(ids)
    await repo.deleteBooks(ids)
    books.value = books.value.filter((b) => !idSet.has(b.id))
    // Limpiar el índice de búsqueda y el caché de portada de cada libro borrado.
    const search = useSearchStore()
    for (const id of ids) {
      void search.removeBookFromIndex(id)
      invalidateCoverCache(id)
    }

    // Borrado opcional en la nube (Storage + cloud_books).
    if (options.deleteCloud) {
      const { deleteEpub } = await import('@/sync/storage')
      const errors: string[] = []
      for (const id of ids) {
        try {
          await deleteEpub(id)
        } catch (e) {
          errors.push(e instanceof Error ? e.message : String(e))
        }
      }
      if (errors.length > 0) {
        // Relanzar agregado para que la UI muestre cuántos fallaron.
        throw new Error(
          `Borrado local OK, pero ${errors.length} de ${ids.length} no se pudieron borrar de la nube: ${errors[0]}`,
        )
      }
    }
  }

  /**
   * Marca/desmarca un libro como favorito. Mutación optimista: actualiza el
   * estado en memoria y persiste. Si la persistencia falla, revierte.
   */
  async function toggleFavorite(id: string) {
    const book = books.value.find((b) => b.id === id)
    if (!book) return
    const prev = book.favorite
    book.favorite = !prev
    try {
      await repo.saveBook(book)
      // Sincronizar el favorito al CRDT cross-device (fuente de verdad para
      // cloud-only). Lazy lookup para evitar dependencia circular de stores.
      const favorites = useFavoritesStore()
      if (book.favorite) await favorites.addFavorite(id)
      else await favorites.removeFavorite(id)
    } catch {
      book.favorite = prev // revertir en caso de error
    }
  }

  /** Busca un libro por id en el estado en memoria. */
  function find(id: string): Book | undefined {
    return books.value.find((b) => b.id === id)
  }

  return {
    books,
    cloudBooks,
    cloudLoading,
    cloudBookIds,
    localIds,
    unifiedBooks,
    visibleBooks,
    visibleBooksPaged,
    hasMoreToLoad,
    loadMore,
    loading,
    importing,
    error,
    search,
    statusFilter,
    sortKey,
    tagFilter,
    collectionFilter,
    shelfFilter,
    sharedFilter,
    favoritesFilter,
    alphaFilter,
    alphaMode,
    density,
    allTags,
    allCollections,
    availableLetters,
    continueReading,
    notStarted,
    inProgress,
    recentlyFinished,
    favorites,
    sharedWithMe,
    load,
    loadCloudBooks,
    importFiles,
    importFromUrl,
    addSharedBook,
    addDownloadedBook,
    remove,
    removeMany,
    toggleFavorite,
    find,
  }
})

// ---------- helpers ----------

/**
 * Construye un `Book` a partir de un EPUB ya descargado/descifrado.
 *
 * Acepta un `Blob` (no `File`) para ser reutilizable por cualquier ruta de
 * importación: archivos locales (`File` es un `Blob`), descargas por URL,
 * feeds OPDS o restauración de backup. El `filename` solo se usa para
 * depuración/trazas; el `fileBlob` persiste el blob original.
 */
async function importOne(blob: Blob, contentHash: string): Promise<Book> {
  const parsed = await parseEpub(blob)
  const now = new Date().toISOString()

  return {
    id: createId(),
    title: parsed.title,
    author: parsed.author,
    coverBlob: parsed.coverBlob,
    fileBlob: blob, // Blob persistido tal cual (File también lo es).
    fileSize: blob.size,
    metadata: {
      publisher: parsed.publisher,
      language: parsed.language,
      isbn: parsed.isbn,
      description: parsed.description,
      // Géneros/categorías del propio EPUB (<dc:subject>), rellenando sin
      // sobrescribir — patrón coherente con `useMetadataEnrichment`. Solo
      // aplica a libros nuevos; los ya importados conservan sus tags.
      tags: parsed.subjects,
      collection: null,
      seriesIndex: null,
      publishDate: parsed.publishDate,
    },
    status: 'unread',
    progress: { cfi: null, percent: 0, lastReadAt: null },
    rating: 0,
    favorite: false,
    dateAdded: now,
    dateFinished: null,
    contentHash,
    shareId: null,
  }
}

/**
 * Persiste un `Book` que acaba de ser importado (con `fileBlob` y
 * `coverBlob` aún en memoria). A partir de v18 los blobs viven en tablas
 * propias: aquí se separan para no escribirlos inline en `books`.
 *
 * El objeto `book` que se pasa queda intacto (los callers pueden seguir
 * usándolo, p. ej. para pasarlo a `indexBook`).
 */
async function persistBookWithBlobs(book: Book): Promise<void> {
  // Primero los blobs (si algo falla por cuota, no queremos un registro
  // `books` huérfano apuntando a blobs inexistentes).
  if (book.fileBlob) {
    await repo.saveBookFile(book.id, book.fileBlob)
  }
  if (book.coverBlob) {
    await repo.saveBookCover(book.id, book.coverBlob)
  }
  // Después la metadata (sin blobs inline).
  await repo.saveBook(stripBlobs(book))
}

/**
 * Devuelve una copia del libro sin los blobs inline. El store en memoria
 * nunca guarda blobs desde v18; esta función garantiza que ni el path de
 * importación los deje colando accidentalmente al insertar en `books.value`.
 */
function stripBlobs(book: Book): Book {
  const { fileBlob: _f, coverBlob: _c, ...meta } = book
  return meta as Book
}

/**
 * Si el usuario tiene activado el auto-upload en Ajustes → Sync, sube el
 * libro recién importado a la nube en background. Fire-and-forget: no
 * bloquea el flujo de importación; si falla, el error se ignora (el libro
 * ya quedó local). El usuario puede reintentar la subida manualmente.
 *
 * Se llama tras `persistBookWithBlobs` en los 3 paths de importación.
 */
function maybeAutoUpload(book: Book): void {
  try {
    const settings = useSettingsStore()
    const auth = useAuthStore()
    if (
      !settings.settings.sync.enabled ||
      !settings.settings.sync.autoUploadOnImport ||
      !auth.isAuthenticated
    ) {
      return
    }
    // Fire-and-forget. Errores silenciados: el libro ya está local, subir
    // a la nube es best-effort. El usuario verá el estado en CloudLibrary.
    void uploadEpub(book).catch(() => {
      /* best-effort: noromper la importación por un fallo de nube */
    })
  } catch {
    /* si los stores no están listos, ignorar */
  }
}

function messageOf(e: unknown): string {
  if (e instanceof InvalidEpubError) return e.message
  if (e instanceof Error) return e.message
  return String(e)
}
